import type { SVGProps } from 'react'

/**
 * Die Zeichen des Schaufensters, an einer Stelle.
 *
 * Vorher stand jeder Einkaufswagen und jede Lupe als eigener Pfad in der Datei,
 * die ihn brauchte — und der Einkaufswagen in der Kopfzeile sah anders aus als
 * der im Menü, weil jemand beim zweiten Mal eine andere Strichstärke nahm.
 *
 * Eingebettetes SVG statt einer Symbolschrift: 300 kB Fremdcode für zwölf
 * Zeichen wären nicht zu rechtfertigen. Alle auf einem 24er-Raster mit 1,7
 * Strich, damit sie nebeneinander wie eine Familie aussehen.
 *
 * Immer aria-hidden: Ein Zeichen steht neben einem Wort oder in einem Knopf mit
 * aria-label. Es selbst sagt nichts, was nicht schon dasteht.
 */

type Art =
  | 'suche'
  | 'konto'
  | 'korb'
  | 'menue'
  | 'schliessen'
  | 'pfeil'
  | 'plus'
  | 'minus'
  | 'paket'
  | 'laden'
  | 'uhr'
  | 'rueckgabe'
  | 'runter'

const PFADE: Record<Art, JSX.Element> = {
  suche: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 4 4" />
    </>
  ),
  konto: (
    <>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  korb: (
    <>
      <path d="M5 8h14l-1.2 11.1a2 2 0 0 1-2 1.9H8.2a2 2 0 0 1-2-1.9L5 8Z" />
      <path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
    </>
  ),
  menue: <path d="M4 7h16M4 12h16M4 17h16" />,
  schliessen: <path d="m6 6 12 12M18 6 6 18" />,
  pfeil: <path d="M5 12h14m-5-5 5 5-5 5" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  paket: (
    <>
      <path d="M21 8v8l-9 5-9-5V8l9-5 9 5Z" />
      <path d="m3 8 9 5 9-5M12 13v8" />
    </>
  ),
  laden: (
    <>
      <path d="M4 9.5 5.2 4h13.6L20 9.5" />
      <path d="M4 9.5a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0" />
      <path d="M5.5 11.5V20h13v-8.5M10 20v-4.5h4V20" />
    </>
  ),
  uhr: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.2 2" />
    </>
  ),
  rueckgabe: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </>
  ),
  runter: <path d="m6 9 6 6 6-6" />,
}

export function Zeichen({
  art,
  groesse = 20,
  ...rest
}: { art: Art; groesse?: number } & Omit<SVGProps<SVGSVGElement>, 'children'>) {
  return (
    <svg
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PFADE[art]}
    </svg>
  )
}
